import { useRef, useState } from "react";
import { useKeenSlider } from "keen-slider/react";
import {
  MotionBox as Box,
  MotionFlex as Flex,
  viewAppearDefault,
} from "utils/motion";
import Image from "components/base/Image";
import Text from "components/base/Text";
import Arrow from "components/icons/HorizontalSliderArrow";
import "keen-slider/keen-slider.min.css";

const heigh = ["320px", null, "460px", "600px"];
const width = ["405px", null, "540px", "660px"];
const widthVert = ["250px", null, "360px", "470px"];
const widthLast = ["425px", null, "640px", "760px", "50vw"];

const WheelControls = (slider) => {
  let touchTimeout;
  let position;
  let wheelActive;

  function dispatch(e, name) {
    position.x -= e.deltaX;
    position.y -= e.deltaY;
    slider.container.dispatchEvent(
      new CustomEvent(name, { detail: { x: position.x, y: position.y } }),
    );
  }

  function wheelStart(e) {
    position = { x: e.pageX, y: e.pageY };
    dispatch(e, "ksDragStart");
  }

  function wheel(e) {
    dispatch(e, "ksDrag");
  }

  function wheelEnd(e) {
    dispatch(e, "ksDragEnd");
  }

  function eventWheel(e) {
    e.preventDefault();
    if (!wheelActive) {
      wheelStart(e);
      wheelActive = true;
    }
    wheel(e);
    clearTimeout(touchTimeout);
    touchTimeout = setTimeout(() => {
      wheelActive = false;
      wheelEnd(e);
    }, 50);
  }

  slider.on("created", () =>
    slider.container.addEventListener("wheel", eventWheel, { passive: true }),
  );
};

const sliderSpringAppear = {
  hidden: { y: 300 },
  visible: { y: 0, transition: { type: "spring", stiffness: 76, damping: 16 } },
};

export default function HorizontalSlideSection({
  title,
  description,
  baseSrc,
  items,
  inverted,
  ...props
}) {
  const ref = useRef(null);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [sliderRef, slider] = useKeenSlider(
    {
      loop: false,
      rubberband: false,
      mode: "snap",
      slides: { perView: "auto", spacing: 32 },
      slideChanged(s) {
        setCurrentSlide(s.track.details.rel);
      },
    },
    [WheelControls],
  );

  return (
    <Box
      ref={ref}
      width="100%"
      bg={inverted ? "white" : "black.900"}
      variants={viewAppearDefault}
      style={{ cursor: "grab" }}
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true }}
      {...props}
      pt={[16, null, 24]}
      pb={[12, null, 32]}
      px={0}
    >
      {title && description && (
        <Flex justify="center">
          <Box
            mx={[6, null, 20]}
            pb="xxl"
            w="100%"
            maxWidth="xl"
            variants={viewAppearDefault}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.4 }}
          >
            <Text
              type="title_lg"
              color="white"
              w={["100%", "100%", "60%"]}
              pb="md"
            >
              {title}
            </Text>
            <Text type="p_lg" color="white" w={["100%", "100%", "60%"]}>
              {description}
            </Text>
          </Box>
        </Flex>
      )}
      <Box
        w="100%"
        h="100%"
        position="relative"
        variants={sliderSpringAppear}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true }}
      >
        <div className="navigation-wrapper">
          <div ref={sliderRef} className="keen-slider">
            {items.map((item, index) => (
              <Flex
                w="100%"
                h="100%"
                className="keen-slider__slide"
                key={item}
                justifyContent="flex-start"
                ml={index === 0 ? [10, null, 20, 20, 20, "15vw"] : 0}
                maxWidth={
                  index === items.length - 1
                    ? widthLast
                    : item.includes("vert")
                      ? widthVert
                      : width
                }
                minWidth={
                  index === items.length - 1
                    ? widthLast
                    : item.includes("vert")
                      ? widthVert
                      : width
                }
              >
                <Image
                  bg="gray.800"
                  height={heigh}
                  width="100%"
                  maxWidth={item.includes("vert") ? widthVert : width}
                  alt={item}
                  objectFit="cover"
                  src={`${baseSrc}${item}.jpg`}
                />
              </Flex>
            ))}
          </div>
          {slider && (
            <>
              <Arrow
                left
                onClick={(e) => e.stopPropagation() || slider.current?.prev()}
                visible={currentSlide > 0}
              />
              <Arrow
                right
                onClick={(e) => e.stopPropagation() || slider.current?.next()}
                visible={currentSlide < items.length - 2}
              />
            </>
          )}
        </div>
      </Box>
    </Box>
  );
}
